import { DatabaseFieldType, RawDatabaseValue } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { Element, Text } from 'domhandler';
import { isEqual, uniq } from 'lodash-es';

import { matchJiraIssueKey } from '../../confluence/jira';
import { getTextContent } from '../../lib/dom';
import { loadJiraIssue } from '../../redux/jira/actions';
import { selectJiraIssueOptional } from '../../redux/jira/selectors';
import { encodeHtml, generateId } from '../../utils';
import type { FieldType } from '../field-types';

function matchJiraIssueKeys(
  arr: string[]
): RawDatabaseValue<DatabaseFieldType.JIRA_ISSUE> {
  return uniq(
    arr.flatMap((it) => {
      const key = matchJiraIssueKey(it);
      return key ? [key] : [];
    })
  ).map((key) => ({ _id: generateId(), key }));
}

export function isNonEmptyJiraIssueValue(
  value: RawDatabaseValue<DatabaseFieldType.JIRA_ISSUE>[0]
): value is RawDatabaseValue<DatabaseFieldType.JIRA_ISSUE>[0] & { key: string } {
  return typeof value === 'object' && value !== null && 'key' in value && !!value.key;
}

const jiraIssueFieldType: FieldType<DatabaseFieldType.JIRA_ISSUE> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  isCsvImportSupported: true,

  renderExportView: ({ field, value }, state) =>
    value
      .flatMap((v) => {
        const resolved =
          isNonEmptyJiraIssueValue(v) &&
          state &&
          selectJiraIssueOptional(state, {
            cloudId: field.settings.cloudId,
            serverId: field.settings.jiraServerId,
            key: v.key,
          });
        if (resolved) {
          return [
            `<span class="confluence-jim-macro jira-issue" data-jira-key="${encodeHtml(
              resolved.key
            )}" style="background:#f5f5f5;border:1px solid #ccc;border-radius:3px;display:inline-block;padding:0 1px 0 3px;line-height:20px">` +
              `<a href="${encodeHtml(resolved.url)}">` +
              `<img src="${encodeHtml(resolved.typeIconUrl)}" alt="${encodeHtml(
                encodeHtml(resolved.type)
              )}" class="icon"/> ` +
              encodeHtml(`${resolved.key}: ${resolved.summary}`) +
              ` <span class="aui-lozenge aui-lozenge-subtle aui-lozenge-${
                resolved.statusCategory === 'done'
                  ? 'success'
                  : resolved.statusCategory === 'in-progress'
                  ? 'current'
                  : 'complete'
              }" style="vertical-align:middle;line-height:100%">${encodeHtml(
                resolved.statusName
              )}</span>` +
              `</a>` +
              `</span>`,
          ];
        } else {
          return [];
        }
      })
      .join('<br/>'),

  parseFromHtml: (el) =>
    matchJiraIssueKeys(
      getTextContent(el, (node) => {
        if (node instanceof Text) {
          return node.nodeValue.replace(/[\r\n\t ]+/g, ' ').trim();
        } else if (node instanceof Element && node.tagName === 'a' && node.attribs.href) {
          return `\n${node.attribs.href}\n`;
        }
      }).split(/\r|\n|\r\n/)
    ),

  getInitialValue: () => [],

  isEmptyValue: ({ value }) => value.filter(isNonEmptyJiraIssueValue).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) =>
    isEqual(
      value1.flatMap((v) => (isNonEmptyJiraIssueValue(v) ? [v.key] : [])),
      value2.flatMap((v) => (isNonEmptyJiraIssueValue(v) ? [v.key] : []))
    ),

  getStringRepresentation: ({ value }) =>
    value.flatMap((v) => (isNonEmptyJiraIssueValue(v) ? [v.key] : [])).join('\n'),

  loadDependencies: ({ field, value }, { dispatch }) => {
    for (const v of value) {
      if (isNonEmptyJiraIssueValue(v)) {
        dispatch(
          loadJiraIssue({
            cloudId: field.settings.cloudId,
            serverId: field.settings.jiraServerId,
            key: v.key,
          })
        );
      }
    }
  },

  convertFrom: {
    text: ({ value }) => matchJiraIssueKeys(value.split(/\r|\n|\r\n/)),

    hyperlink: ({ value }) => matchJiraIssueKeys(value.map((v) => v.url)),
  },

  convertTo: {
    text: ({ value }) =>
      value.flatMap((v) => (isNonEmptyJiraIssueValue(v) ? [v.key] : [])).join('\n'),

    hyperlink: ({ field, value }, state) =>
      value.flatMap((v) => {
        const resolved =
          isNonEmptyJiraIssueValue(v) &&
          state &&
          selectJiraIssueOptional(state, {
            cloudId: field.settings.cloudId,
            serverId: field.settings.jiraServerId,
            key: v.key,
          });
        return resolved
          ? [
              {
                url: resolved.url,
                label: `${resolved.key}: ${resolved.summary}`,
                _id: v._id,
              },
            ]
          : [];
      }),
  },
};

export default jiraIssueFieldType;
